// =====================================================
// DASHBOARD STATS — Agrégation (fonctions pures, testables)
// =====================================================
// Chargées une fois via le hook (fetchAllPages), les lignes brutes sont agrégées
// ici. Aucune mutation. Aucune dépendance React → 100% testable en vitest.

import type {
  DateWindow,
  DeltaValue,
  EditionRef,
  GlobalKpis,
  KpiKey,
  KpiValue,
  RawRows,
} from './types';
import { isIdCrew, isPiCrew, PR_CONTACT_STATUS } from './nomenclature';
import { inWindow } from './periods';

/**
 * Compte TOUS les BPI générés d'une édition dans une fenêtre (réunion 22/07).
 * Source = soumissions aux formulaires de préinscription (`generates_bpi`),
 * payées OU non : le client veut « le nombre de formulaires remplis », pas les
 * paiements payés. (Le dénominateur payé reste dans conversion.ts / rawCounts.)
 */
function countBpi(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.bpiFormSubmissions.filter(
    (f) => f.product_id === editionId && inWindow(f.created_at, w)
  ).length;
}

function countPi(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.crews.filter(
    (c) => c.product_id === editionId && isPiCrew(c) && inWindow(c.created_at, w)
  ).length;
}

function countId(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.crews.filter(
    (c) => c.product_id === editionId && isIdCrew(c) && inWindow(c.created_at, w)
  ).length;
}

/** Dérogations EN ATTENTE (crews.derogation_pending) d'une édition dans la fenêtre. */
function countDerogations(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.crews.filter(
    (c) => c.product_id === editionId && c.derogation_pending === true && inWindow(c.created_at, w)
  ).length;
}

/** Dérogations ACCEPTÉES (crews.has_derogation) d'une édition dans la fenêtre (22/07). */
function countDerogationsAcceptees(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.crews.filter(
    (c) => c.product_id === editionId && c.has_derogation === true && inWindow(c.created_at, w)
  ).length;
}

function countPiAttente(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.bpiFormSubmissions.filter(
    (f) => f.product_id === editionId && f.converted_at == null && inWindow(f.created_at, w)
  ).length;
}

/** PR = prospects GLOBAUX (non scopés édition) — cf. nomenclature FLAG 3. */
function countPr(rows: RawRows, w: DateWindow): number {
  return rows.prospects.filter((c) => c.status === PR_CONTACT_STATUS && inWindow(c.created_at, w))
    .length;
}

/**
 * Construit une DeltaValue (framework N-1 réutilisable, sans clé KPI).
 * @param prevValue null quand aucune édition N-1 n'est appariée (n1Unavailable).
 */
export function buildDelta(value: number, prevValue: number | null): DeltaValue {
  if (prevValue == null) {
    return { value, prevValue: null, deltaAbs: null, deltaPct: null, n1Unavailable: true };
  }
  const deltaAbs = value - prevValue;
  let deltaPct: number | null;
  if (prevValue === 0) {
    // N-1 = 0 : +100% si N > 0 (Infinity marqueur), 0% si N = 0 aussi.
    deltaPct = value === 0 ? 0 : Infinity;
  } else {
    deltaPct = (deltaAbs / prevValue) * 100;
  }
  return { value, prevValue, deltaAbs, deltaPct, n1Unavailable: false };
}

/**
 * Construit une KpiValue = DeltaValue + clé de l'indicateur.
 * @param prevValue null quand aucune édition N-1 n'est appariée (n1Unavailable).
 */
export function buildKpiValue(key: KpiKey, value: number, prevValue: number | null): KpiValue {
  return { key, ...buildDelta(value, prevValue) };
}

export interface ComputeParams {
  rows: RawRows;
  edition: EditionRef;
  /** Fenêtre N = plage de dates sélectionnée [début, fin]. */
  window: DateWindow;
  /** Fenêtre N-1 = même plage décalée d'un an (cf. shiftWindowYears). */
  prevWindow: DateWindow;
}

/**
 * Calcule les indicateurs globaux pour l'édition N, avec comparaison N-1.
 * RETOUCHE 1 : N = compte sur la plage `window` (édition N) ; N-1 = compte sur la
 * plage `prevWindow` (= même plage décalée d'un an) de l'édition N-1 appariée.
 * Sans édition N-1 appariée → les KPI édition affichent N/A. PR est GLOBAL (non
 * scopé édition) mais a désormais un N-1 = prospects de la plage année-1.
 */
export function computeGlobalKpis({
  rows,
  edition,
  window: wN,
  prevWindow: wPrev,
}: ComputeParams): GlobalKpis {
  const hasN1 = edition.prevEditionId != null;
  const prevEdition = edition.prevEditionId;

  const mk = (key: KpiKey, valFn: (eid: string, w: DateWindow) => number): KpiValue => {
    const value = valFn(edition.id, wN);
    const prev = hasN1 && prevEdition ? valFn(prevEdition, wPrev) : null;
    return buildKpiValue(key, value, prev);
  };

  return {
    bpi: mk('bpi', (eid, w) => countBpi(rows, eid, w)),
    pi: mk('pi', (eid, w) => countPi(rows, eid, w)),
    id: mk('id', (eid, w) => countId(rows, eid, w)),
    // PR : GLOBAL (non scopé édition) → N sur la plage, N-1 sur la plage année-1.
    pr: buildKpiValue('pr', countPr(rows, wN), countPr(rows, wPrev)),
    derogations: mk('derogations', (eid, w) => countDerogations(rows, eid, w)),
    derogations_acceptees: mk('derogations_acceptees', (eid, w) =>
      countDerogationsAcceptees(rows, eid, w)
    ),
    pi_attente: mk('pi_attente', (eid, w) => countPiAttente(rows, eid, w)),
  };
}
